import { useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

import { useDispatch, useSelector } from 'BMapsRedux';
import { App } from 'BMapsSrc/BMapsApp';
import { JobResultDestinations, lookupJobDefinition } from 'BMapsSrc/jobman';
import { updateDisplayedMdJobs } from 'BMapsRedux/jobs/access';
import { cancelJob, fetchJobs } from 'BMapsRedux/jobs/thunks';
import BMapsTooltip from 'BMapsUI/BMapsTooltip';
import DialogContentSection from 'BMapsUI/common/DialogContentSection';
import LoadingIconButton from 'BMapsUI/common/LoadingIconButton';
import { userConfirmation } from 'BMapsSrc/utils';
import { JobsGrid } from 'BMapsDialog/ImportPane/ImportProteinPane/Tabs/JobsStructuresTab';
import { openMdSidePanel } from '../MdSidePanel';

/**
 * MD jobs: every STORMM / GROMACS / OpenMM job (running, done, failed) with progress, a
 * Cancel action while running, and Load (to the Results tab) when done.
 */
export default function MdJobs({ initData, highlightedJobId, selected }) {
    const { jobs, localJobState, status } = useSelector((state) => state.jobs);
    const connection = App.PrimaryDataConnection;

    const dispatch = useDispatch();
    const retrieveJobs = () => dispatch(fetchJobs());
    const [flashId, setFlashId] = useState(null);
    const [errors, setErrors] = useState([]);
    const mdJobs = useMemo(
        () => jobs.filter(
            (job) => lookupJobDefinition(job.jobtype)
                ?.resultDestination === JobResultDestinations.MOLECULAR_DYNAMICS
        ),
        [jobs]
    );
    const loadJobsList = async () => {
        const { error } = await retrieveJobs();
        if (error) {
            setErrors([error]);
        } else {
            setErrors([]);
        }
    };

    // Auto-fetch jobs on mount if not already loaded
    useEffect(() => {
        if (status === 'unloaded') {
            loadJobsList();
        }
    }, [status]);

    // Animates the flash when a new job is created, so that it can be easily visible
    useEffect(() => {
        if (!highlightedJobId) return () => null;
        setFlashId(highlightedJobId);

        const timer = setTimeout(() => setFlashId(null), 500);
        return () => {
            clearTimeout(timer);
        };
    }, [highlightedJobId]);

    function setActiveJobs(newJobs) {
        updateDisplayedMdJobs(newJobs);
        openMdSidePanel('md_results_tab', { ...initData });
    }

    async function cancelJobs(jobids) {
        const chosen = jobids.map((id) => mdJobs.find((j) => j.jobid === id)).filter(Boolean);
        if (chosen.length === 0) return;
        const names = chosen.map((job) => job.requestmeta?.jobName || job.jobid).join('\n');
        const ok = await userConfirmation(
            'Cancel MD job?',
            `Stop the compute for:\n${names}\n\nThe simulation stops at once and the job shows as failed with the reason "cancelled".`
        );
        if (!ok) return;
        const failures = [];
        for (const job of chosen) {
            // eslint-disable-next-line no-await-in-loop
            const { error } = await dispatch(cancelJob(job.jobid));
            if (error) failures.push(`${job.requestmeta?.jobName || job.jobid}: ${error}`);
        }
        setErrors(failures);
    }

    if (!selected) return null;

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                maxHeight: '100%',
                overflow: 'hidden',
            }}
        >
            <DialogContentSection
                title="Molecular Dynamics Jobs"
                description="STORMM and GROMACS MD jobs"
                disablePaddingTop
                style={{
                    marginBottom: '1rem',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                    overflow: 'hidden',
                }}
                titleIcon={(
                    <BMapsTooltip title="Refresh Jobs List">
                        <LoadingIconButton Icon={RefreshIcon} load={loadJobsList} />
                    </BMapsTooltip>
                )}
                contentStyle={{
                    display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden',
                }}
            >
                <JobsGrid
                    jobs={mdJobs}
                    userSpecifiedSelectivity="individual"
                    dataConnection={connection}
                    flashId={flashId}
                    localJobState={localJobState}
                    retrieveJobs={retrieveJobs}
                    errors={errors}
                    setActiveJobs={setActiveJobs}
                    cancelJobs={cancelJobs}
                    dataGridProps={{
                        rowHeight: 60,
                        columnVisibilityModel: {
                            jobtype: false,
                        },
                    }}
                    columnControl={{
                        exclude: ['delivered'],
                    }}
                />
            </DialogContentSection>
        </Box>
    );
}
